import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2, User, CreditCard, Receipt, Check } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";
import { computeUnitConflicts } from "@/lib/availability";

const today10 = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const nightsBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CHANNELS = ["Direct", "Atiti", "Airbnb", "Booking.com", "MakeMyTrip", "Walk-in"];
const BUSINESS_SOURCES = ["Direct Website", "Walk-in", "Travel Agent", "Corporate", "OTA", "Repeat Guest"];
const SEGMENTS = ["Leisure", "Business", "Wedding", "Group", "Corporate", "Family"];

const emptyRoom = () => ({ unit_id: "", rate: "", adults: 1, children: 0 });

export default function NewReservation({ pid, propertyName, onClose, onCreated, pastAllowed = true }) {
  const [meta, setMeta] = useState(null);
  const [travelAgents, setTravelAgents] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [taId, setTaId] = useState("");
  const [busy, setBusy] = useState(false);
  const [ci, setCi] = useState(today10());
  const [co, setCo] = useState(plusDays(today10(), 1));
  const [ciTime, setCiTime] = useState("11:00");
  const [coTime, setCoTime] = useState("10:00");
  const [rooms, setRooms] = useState([emptyRoom()]);
  const [guest, setGuest] = useState({ guest: "", mobile: "", email: "", gender: "", address: "", id_type: "", id_number: "", nationality: "India", purpose_of_visit: "Leisure" });
  const [source, setSource] = useState("Direct");
  const [businessSource, setBusinessSource] = useState("Direct Website");
  const [segment, setSegment] = useState("Leisure");
  const [salesPerson, setSalesPerson] = useState("");
  const [taxRate, setTaxRate] = useState(12);
  const [taxAuto, setTaxAuto] = useState(true);           // auto-derive from slabs when true
  const [billTo, setBillTo] = useState("Guest");
  const [companyName, setCompanyName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);
  const [rateInclusive, setRateInclusive] = useState(true);   // per-night rate is tax-inclusive by default (negotiation mode)
  const [payment, setPayment] = useState({ amount: "", mode: "UPI", remark: "" });
  const [lookupHint, setLookupHint] = useState("");

  // Returning guest auto-fill — when the operator types a phone or email that
  // matches an existing guest across the admin's properties, pre-fill every
  // guest field they've entered before (still editable so this booking's
  // details can be corrected on the fly).
  const doLookup = async (field, value) => {
    if (!value || value.length < 4) return;
    try {
      const { data } = await api.get(`/pms/guests/lookup`, { params: { [field]: value } });
      if (data.found && data.guest) {
        setGuest((prev) => ({
          ...prev,
          guest: prev.guest || data.guest.guest || "",
          mobile: prev.mobile || data.guest.mobile || "",
          email: prev.email || data.guest.email || "",
          gender: prev.gender || data.guest.gender || "",
          address: prev.address || data.guest.address || "",
          id_type: prev.id_type || data.guest.id_type || "",
          id_number: prev.id_number || data.guest.id_number || "",
          nationality: prev.nationality || data.guest.nationality || "India",
          purpose_of_visit: prev.purpose_of_visit || data.guest.purpose_of_visit || "Leisure",
        }));
        if (data.guest.bill_to && data.guest.bill_to !== "Guest") setBillTo(data.guest.bill_to);
        if (data.guest.company_name) setCompanyName(data.guest.company_name);
        if (data.guest.tax_id) setTaxId(data.guest.tax_id);
        setLookupHint(`Returning guest · last stayed ${data.guest.last_stay || "recently"} · fields auto-filled`);
        toast.success("Returning guest — details auto-filled");
      }
    } catch {}
  };

  useEffect(() => {
    api.get(`/pms/properties/${pid}/reservations`).then(({ data }) => {
      setMeta(data);
      // Batch-2 Item 6: default check-in / check-out anchor on the property's
      // current Night Audit Operating Day, not the system clock.
      if (data?.today) {
        setCi(data.today);
        setCo(plusDays(data.today, 1));
      }
    }).catch((e) => toast.error(errMsg(e)));
    api.get(`/pms/properties/${pid}/travel-agents`).then(({ data }) => setTravelAgents((data.agents || []).filter((a) => a.active)))
      .catch(() => {});
    api.get(`/pms/admin/taxation`).then(({ data }) => setTaxSlabs(data.slabs || []))
      .catch(() => {});
  }, [pid]);

  const ta = useMemo(() => travelAgents.find((a) => a.id === taId), [taId, travelAgents]);
  const nights = useMemo(() => nightsBetween(ci, co), [ci, co]);
  // Batch-2 Item 2 (bug #1): per-unit slab lookup — each unit's own nightly
  // rate picks its own slab (0% / 5% / 18% per the confirmed structure,
  // sourced dynamically from `/pms/admin/taxation`). Falls back to the
  // property-default when no slab matches.
  const slabForRate = (nightlyRate) => {
    const n = Number(nightlyRate) || 0;
    if (!taxSlabs || !taxSlabs.length) return null;
    for (const s of taxSlabs) {
      const min = Number(s.min || 0);
      const max = s.max == null ? Infinity : Number(s.max);
      if (n >= min && n < max) return Number(s.rate || 0);
    }
    return Number(taxSlabs[taxSlabs.length - 1]?.rate || 0);
  };
  // Direct-only availability (host spec §3, Feb 2026). Shared helper so
  // this picker and the backend `_check_availability` apply the same rule.
  // §A (host spec Feb 2026, cumulative-availability) — one conflict map
  // PER row. Each row's map includes the global DB conflicts PLUS synthetic
  // "ghost" bookings for every unit picked on OTHER rows of this form,
  // so direct-neighbour propagation naturally hides them from this row's
  // dropdown. Row N never sees itself as a conflict.
  const unitConflictsPerRow = useMemo(() => {
    if (!meta) return [];
    const dbBookings = meta.reservations || [];
    const links = meta.unit_links || [];
    const manual = meta.manual_blocks || [];
    return rooms.map((_, selfIdx) => {
      const ghostBookings = rooms
        .map((x, i) => (i !== selfIdx && x.unit_id)
          ? { id: `ghost-${x.unit_id}`, unit_id: x.unit_id,
              checkin: ci, checkout: co, status: "upcoming",
              guest: "picked on another line" }
          : null)
        .filter(Boolean);
      return computeUnitConflicts({
        bookings: [...dbBookings, ...ghostBookings],
        manualBlocks: manual, unitLinks: links, ci, co,
      });
    });
  }, [meta, ci, co, rooms]);
  // Global map (unchanged signature) — used by the dates-changed guard
  // below and by the "N free · M booked" header. Represents DB-only
  // conflicts, no ghosts.
  const unitConflicts = useMemo(() => {
    if (!meta) return new Map();
    return computeUnitConflicts({
      bookings: meta.reservations || [],
      manualBlocks: meta.manual_blocks || [],
      unitLinks: meta.unit_links || [],
      ci, co,
    });
  }, [meta, ci, co]);
  // Auto-clear any picked unit that just became unavailable when dates changed
  useEffect(() => {
    setRooms((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (r.unit_id && unitConflicts.has(r.unit_id)) { changed = true; return { ...r, unit_id: "" }; }
        return r;
      });
      if (changed) toast.warning("Some picked units are booked for the new dates — please choose another unit.");
      return changed ? next : prev;
    });
  }, [unitConflicts]);
  // Gross = sum of what user typed × nights. If rate is inclusive, gross IS the final total;
  // if exclusive, gross is the pre-tax subtotal and tax is added on top.
  const gross = rooms.reduce((s, r) => s + (parseFloat(r.rate) || 0) * nights, 0);
  const effectiveTaxRate = taxExempt ? 0 : (Number(taxRate) || 0);
  const subtotal = rateInclusive && effectiveTaxRate > 0
    ? Math.round((gross / (1 + effectiveTaxRate / 100)) * 100) / 100
    : gross;
  // Auto-pick tax slab from the highest nightly rate across all room lines
  const maxNightly = useMemo(
    () => rooms.reduce((m, r) => Math.max(m, parseFloat(r.rate) || 0), 0),
    [rooms]
  );
  useEffect(() => {
    if (!taxAuto || !taxSlabs.length || taxExempt) return;
    const slab = taxSlabs.find((s) => maxNightly >= (s.min || 0) && (s.max == null || maxNightly < s.max));
    if (slab) setTaxRate(Number(slab.rate) || 0);
  }, [maxNightly, taxSlabs, taxAuto, taxExempt]);
  const taCommission = useMemo(() => {
    if (!ta) return 0;
    if (ta.commission_plan === "flat") return Number(ta.commission_value || 0);
    return Math.round(subtotal * Number(ta.commission_value || 0)) / 100;
  }, [ta, subtotal]);
  const tax = rateInclusive ? Math.round((gross - subtotal) * 100) / 100 : Math.round(subtotal * effectiveTaxRate) / 100;
  const total = rateInclusive ? gross : subtotal + tax;
  const paidNow = parseFloat(payment.amount) || 0;
  const due = Math.max(total - paidNow, 0);

  const setRoom = (i, field, v) => setRooms(rooms.map((r, idx) => idx === i ? { ...r, [field]: v } : r));
  const addRoom = () => setRooms([...rooms, emptyRoom()]);
  const delRoom = (i) => setRooms(rooms.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!guest.guest.trim()) return toast.error("Enter the guest name");
    if (rooms.some((r) => !r.unit_id || !parseFloat(r.rate))) return toast.error("Every line needs a unit and a rate");
    if (co <= ci) return toast.error("Check-out must be after check-in");
    if (!pastAllowed && ci < (meta?.today || today10())) return toast.error("Quick Reservation cannot be for a past date. Use Insert Transaction to record a past booking retroactively.");
    if (billTo === "Company" && !companyName.trim()) return toast.error("Company name is required when billing to a company");
    setBusy(true);
    const created = [];
    // If multiple rooms are booked at once, stamp them with a shared group_id so
    // the Invoice / Voucher can render a consolidated document later.
    const groupId = rooms.length > 1 ? (crypto.randomUUID?.() || `grp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`) : "";
    // Resolve billing details — TA takes precedence when picked
    const resolvedCompanyName = billTo === "Travel Agent" && ta ? ta.name : (billTo === "Company" ? companyName.trim() : "");
    const resolvedTaxId = billTo === "Travel Agent" && ta ? (ta.tax_id || "") : (billTo === "Company" ? taxId.trim() : "");
    try {
      for (let idx = 0; idx < rooms.length; idx++) {
        const r = rooms[idx];
        // Batch-2 Item 2: each unit gets its OWN tax_rate based on that unit's
        // nightly rate slab — not one uniform rate across the reservation.
        const enteredRate = parseFloat(r.rate) || 0;
        const perUnitRate = taxAuto && !taxExempt ? (slabForRate(enteredRate) ?? effectiveTaxRate) : effectiveTaxRate;
        const perUnitEff = taxExempt ? 0 : perUnitRate;
        const netRate = rateInclusive && perUnitEff > 0
          ? Math.round((enteredRate / (1 + perUnitEff / 100)) * 100) / 100
          : enteredRate;
        const { data: b } = await api.post(`/pms/properties/${pid}/bookings`, {
          unit_id: r.unit_id, guest: guest.guest.trim(), mobile: guest.mobile,
          checkin: ci, checkout: co,
          rate: netRate, adults: r.adults, children: r.children,
          reservation_type: "Confirm Booking",
          bill_to: billTo, company_name: resolvedCompanyName, tax_id: resolvedTaxId,
          tax_rate: perUnitEff, tax_exempt: taxExempt, rate_inclusive: rateInclusive,
          business_source: businessSource, segment, sales_person: salesPerson,
          ci_time: ciTime, co_time: coTime,
          reservation_group_id: groupId, is_parent: groupId ? idx === 0 : false,
        });
        created.push(b);
        // Loud warning if the booking couldn't reach Channex — same
        // reason path as QuickReservation.
        if (b?.channex_sync && b.channex_sync.status !== "queued") {
          const reason = b.channex_sync.reason;
          const label = reason === "not_linked_property"
            ? "Property not linked to OTA Connectivity"
            : reason === "not_linked_unit"
            ? "Unit isn't mapped to a room type on OTA Connectivity"
            : (b.channex_sync.message || "OTA sync skipped");
          toast.warning(`⚠ ${label} — this booking is NOT pushed to your OTA calendar. Fix it in the OTA Connectivity tab.`, {
            duration: 12000, id: `channex-skip-${b.id}`,
          });
        }
        // Patch guest details
        const guestPatch = {};
        ["email", "gender", "address", "id_type", "id_number", "nationality", "purpose_of_visit"].forEach((k) => {
          if (guest[k]) guestPatch[k] = guest[k];
        });
        if (Object.keys(guestPatch).length) {
          try { await api.patch(`/pms/bookings/${b.id}/guest`, guestPatch); } catch (_) {}
        }
        // First-room payment if paidNow > 0
        if (created.length === 1 && paidNow > 0) {
          try {
            await api.post(`/pms/bookings/${b.id}/payments`, { amount: paidNow, mode: payment.mode, remark: payment.remark || `Advance · ${ta?.name || businessSource}` });
          } catch (_) {}
        }
      }
      toast.success(`${rooms.length} unit${rooms.length > 1 ? "s" : ""} reserved for ${guest.guest} · ${nights}n · Total ₹${fmt(total)}${paidNow > 0 ? ` (paid ₹${fmt(paidNow)})` : ""}`);
      // Log commission if a travel agent was selected
      if (ta && taCommission > 0 && created.length) {
        try {
          await api.post(`/pms/properties/${pid}/travel-agents/${ta.id}/log-commission`, {
            booking_id: created[0].id, guest: guest.guest.trim(), revenue: subtotal,
          });
          toast.success(`₹${fmt(taCommission)} commission logged for ${ta.name}`);
        } catch (_) {}
      }
      onCreated && onCreated(created[0]);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
      // Rollback: cancel any successfully-created bookings so we don't leave orphans
      for (const b of created) {
        try { await api.post(`/pms/bookings/${b.id}/status`, { status: "cancelled", reason: "Wizard rollback" }); } catch (_) {}
      }
      // §C — refresh the parent's reservation list so the picker's cached
      // `meta.reservations` picks up (a) any bookings that DID succeed and
      // (b) the fresh cancelled rows from the rollback above. Without
      // this, a retry inside the same open form will still see stale
      // "free" slots and 409 again.
      try { onCreated && onCreated(null); } catch (_) {}
    }
    setBusy(false);
  };

  if (!meta) return (
    <Portal><div className="fixed inset-0 bg-white z-[90] flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={30} /></div></Portal>
  );

  return (
    <Portal>
    <div className="fixed inset-0 z-[90] bg-slate-50 overflow-y-auto">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-4 flex items-center gap-4">
        <div>
          <h1 className="font-display font-black text-slate-900 text-xl">{pastAllowed ? "Insert Transaction" : "Quick Reservation"}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{propertyName} · {pastAllowed ? "record a past, present or future booking" : "new reservation (present or future dates)"}</p>
        </div>
        <button data-testid="new-res-close" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-900 rounded-full w-9 h-9 flex items-center justify-center hover:bg-slate-100"><X size={18} /></button>
      </div>

      <div className="max-w-[1400px] mx-auto p-8 grid grid-cols-1 xl:grid-cols-[1fr,380px] gap-6">
        <div className="space-y-5">
          {/* Dates & sources */}
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 text-sm mb-4">Stay & Source</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Check-in"><input data-testid="nr-checkin" type="date" min={pastAllowed ? undefined : (meta?.today || today10())} value={ci} onChange={(e) => {
                const v = e.target.value;
                setCi(v);
                // Auto-advance checkout: if the current checkout is on or
                // before the new check-in, bump it to check-in + 1 night.
                // The user can still manually push it further ahead, this
                // just guarantees the booking is at least one night long.
                if (!co || co <= v) setCo(plusDays(v, 1));
              }} className={inp} /></Field>
              <Field label="Check-in time"><input type="time" value={ciTime} onChange={(e) => setCiTime(e.target.value)} className={inp} /></Field>
              <Field label="Check-out"><input data-testid="nr-checkout" type="date" min={plusDays(ci || meta?.today || today10(), 1)} value={co} onChange={(e) => {
                const v = e.target.value;
                // Hard-guarantee check-out > check-in, even if the user
                // somehow types a value through DevTools or a keyboard.
                setCo(v > ci ? v : plusDays(ci, 1));
              }} className={inp} /></Field>
              <Field label="Check-out time"><input type="time" value={coTime} onChange={(e) => setCoTime(e.target.value)} className={inp} /></Field>
              <Field label="Booking source"><select data-testid="nr-source" value={source} onChange={(e) => setSource(e.target.value)} className={inp}>{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Business source"><select value={businessSource} onChange={(e) => setBusinessSource(e.target.value)} className={inp}>{BUSINESS_SOURCES.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Market segment"><select value={segment} onChange={(e) => setSegment(e.target.value)} className={inp}>{SEGMENTS.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label="Sales person"><input value={salesPerson} onChange={(e) => setSalesPerson(e.target.value)} placeholder="Optional" className={inp} /></Field>
            </div>
          </section>

          {/* Units section */}
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex justify-between items-start mb-4 gap-3 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-slate-900 text-sm flex items-center gap-2">
                  <span className="h-6 w-6 rounded-md bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center text-[10px] font-bold">{rooms.length}</span>
                  Unit{rooms.length > 1 ? "s" : ""} · {nights} night{nights > 1 ? "s" : ""}
                </h3>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Only units free between <b className="text-slate-600">{ci}</b> and <b className="text-slate-600">{co}</b> appear in the picker.
                  {" "}
                  <span className="text-slate-500">
                    <b className="text-emerald-600">
                      {Math.max(0, (meta?.bookable_units?.length || 0) - unitConflicts.size)}
                    </b> of {meta?.bookable_units?.length || 0} free
                    {unitConflicts.size > 0 && <> · <b className="text-amber-600">{unitConflicts.size}</b> booked</>}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div data-testid="nr-rate-mode-toggle" className="inline-flex bg-slate-100 rounded-full p-0.5 text-[10px] font-semibold">
                  <button
                    type="button"
                    data-testid="nr-rate-inclusive"
                    onClick={() => setRateInclusive(true)}
                    className={`px-3 py-1 rounded-full transition-colors ${rateInclusive ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
                    title="The rate you type is the FINAL per-night amount the guest pays. Tax is back-calculated from it."
                  >
                    Rate incl. taxes
                  </button>
                  <button
                    type="button"
                    data-testid="nr-rate-exclusive"
                    onClick={() => setRateInclusive(false)}
                    className={`px-3 py-1 rounded-full transition-colors ${!rateInclusive ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
                    title="The rate you type is BEFORE tax. Tax is added on top."
                  >
                    Rate excl. taxes
                  </button>
                </div>
                <button
                  data-testid="nr-add-room"
                  onClick={addRoom}
                  className="text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA] bg-[#EEF2FF] hover:bg-[#E0E7FF] rounded-full px-3 py-1.5 flex items-center gap-1"
                >
                  <Plus size={13} /> Add unit
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mb-3 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
              {rateInclusive ? (
                <>💡 <b>Negotiation mode</b> — the rate you enter is the final per-night amount the guest pays. Tax at <b>{effectiveTaxRate}%</b> is back-solved (e.g. ₹7,000 incl. → ₹{fmt(effectiveTaxRate > 0 ? 7000 / (1 + effectiveTaxRate / 100) : 7000)} room + ₹{fmt(effectiveTaxRate > 0 ? 7000 - 7000 / (1 + effectiveTaxRate / 100) : 0)} tax).</>
              ) : (
                <>💡 <b>Rack-rate mode</b> — the rate you enter is pre-tax. Tax at <b>{effectiveTaxRate}%</b> will be added on top.</>
              )}
            </p>
            <div className="space-y-3">
              {rooms.map((r, i) => {
                const line = (parseFloat(r.rate) || 0) * nights;
                const selected = (meta.bookable_units || []).find((u) => u.unit_id === r.unit_id);
                const usedIds = rooms.map((x, idx) => idx !== i ? x.unit_id : null).filter(Boolean);
                // §A — per-row conflict map includes direct neighbours of units picked on other rows.
                const rowConflicts = unitConflictsPerRow[i] || unitConflicts;
                // Filter out (a) units already picked on another row, and (b) units that
                // conflict with the selected date range OR with another row's selection.
                const available = (meta.bookable_units || []).filter(
                  (u) => !usedIds.includes(u.unit_id) && !rowConflicts.has(u.unit_id)
                );
                const conflicted = (meta.bookable_units || []).filter((u) => rowConflicts.has(u.unit_id));
                return (
                  <div
                    key={i}
                    data-testid={`nr-unit-card-${i}`}
                    className={`relative rounded-xl border-2 transition-all ${
                      selected
                        ? "border-[#4F46E5] bg-gradient-to-br from-indigo-50/60 to-white shadow-sm"
                        : "border-dashed border-slate-200 bg-slate-50/40 hover:border-slate-300"
                    } p-4`}
                  >
                    <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shadow">{i + 1}</div>

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr),minmax(0,1fr),80px,80px,minmax(120px,1fr),36px] gap-3 items-end">
                      {/* Unit picker */}
                      <div className="min-w-0">
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Unit</label>
                        {selected ? (
                          <button
                            type="button"
                            data-testid={`nr-unit-selected-${i}`}
                            onClick={() => setRoom(i, "unit_id", "")}
                            className="mt-1 w-full flex items-center gap-2 rounded-lg border-2 border-[#4F46E5] bg-white px-3 py-2 text-left transition-shadow hover:shadow"
                            title="Click to change unit"
                          >
                            <span className="h-8 w-8 rounded-md bg-[#4F46E5] text-white text-xs font-bold flex items-center justify-center shrink-0">
                              {String(selected.name || "?").slice(0, 2).toUpperCase()}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-bold text-slate-900 truncate">Unit {selected.name}</span>
                              <span className="block text-[10px] text-slate-500 uppercase tracking-wider">
                                {selected.mapped ? "Channel-mapped" : "Direct only"} · tap to change
                              </span>
                            </span>
                            <Check size={14} className="text-[#4F46E5] shrink-0" />
                          </button>
                        ) : (
                          <select
                            data-testid={`nr-room-${i}`}
                            value={r.unit_id}
                            onChange={(e) => setRoom(i, "unit_id", e.target.value)}
                            className={`${inp} mt-1 border-2 ${!r.unit_id && rooms.length > 1 ? "border-slate-200" : "border-slate-300"}`}
                          >
                            <option value="">
                              {available.length > 0
                                ? `Pick a unit… (${available.length} free)`
                                : "No units free for these dates"}
                            </option>
                            {available.map((u) => (
                              <option key={u.unit_id} value={u.unit_id}>
                                Unit {u.name}{u.mapped === false ? " · direct only" : ""}
                              </option>
                            ))}
                            {conflicted.length > 0 && (
                              <optgroup label={`── Unavailable ${ci} → ${co} ──`}>
                                {conflicted.map((u) => {
                                  const c = rowConflicts.get(u.unit_id)?.[0];
                                  const label = c?.reason === "connected-unit-booked"
                                    ? `Unit ${u.name} · Blocked — connected unit booked (${c.checkin}→${c.checkout})`
                                    : c?.reason === "blocked"
                                    ? `Unit ${u.name} · Blocked (${c?.guest || "manual"})`
                                    : c?.reason === "connected-unit-blocked"
                                    ? `Unit ${u.name} · Blocked — connected unit blocked`
                                    : `Unit ${u.name} · Booked ${c?.checkin}→${c?.checkout} (${c?.guest || "guest"})`;
                                  return (
                                    <option key={u.unit_id} value="" disabled>
                                      {label}
                                    </option>
                                  );
                                })}
                              </optgroup>
                            )}
                          </select>
                        )}
                      </div>

                      {/* Rate */}
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                          Rate / night ₹ <span className="text-slate-300 normal-case">{rateInclusive ? "(incl.)" : "(excl.)"}</span>
                        </label>
                        <input
                          data-testid={`nr-rate-${i}`}
                          inputMode="decimal"
                          value={r.rate}
                          onChange={(e) => setRoom(i, "rate", e.target.value.replace(/[^\d.]/g, ""))}
                          placeholder="0"
                          className={`${inp} mt-1 font-mono text-right text-base`}
                        />
                      </div>

                      {/* Adults */}
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Adult</label>
                        <input
                          type="number"
                          min="1"
                          value={r.adults}
                          onChange={(e) => setRoom(i, "adults", parseInt(e.target.value || "1", 10))}
                          className={`${inp} mt-1 font-mono text-right`}
                        />
                      </div>

                      {/* Child */}
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Child</label>
                        <input
                          type="number"
                          min="0"
                          value={r.children}
                          onChange={(e) => setRoom(i, "children", parseInt(e.target.value || "0", 10))}
                          className={`${inp} mt-1 font-mono text-right`}
                        />
                      </div>

                      {/* Line total */}
                      <div className="text-right">
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Line total ₹</label>
                        <div className={`mt-1 font-mono text-base font-bold ${line > 0 ? "text-slate-900" : "text-slate-300"}`}>{fmt(line)}</div>
                        <div className="text-[9px] text-slate-400 -mt-0.5">{nights}n × ₹{fmt(parseFloat(r.rate) || 0)}</div>
                      </div>

                      {/* Delete */}
                      <button
                        onClick={() => delRoom(i)}
                        disabled={rooms.length === 1}
                        title={rooms.length === 1 ? "At least one unit is required" : "Remove this unit line"}
                        className="h-9 w-9 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Guest info */}
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 text-sm mb-4 flex items-center gap-2"><User size={14} className="brand-pink" /> Guest information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Name *"><input data-testid="nr-guest-name" value={guest.guest} onChange={(e) => setGuest({ ...guest, guest: e.target.value })} className={inp} /></Field>
              <Field label="Mobile"><input data-testid="nr-guest-mobile" value={guest.mobile} onChange={(e) => setGuest({ ...guest, mobile: e.target.value })} onBlur={(e) => doLookup("phone", e.target.value)} className={inp} /></Field>
              <Field label="Email"><input data-testid="nr-guest-email" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} onBlur={(e) => doLookup("email", e.target.value)} className={inp} /></Field>
              <Field label="Gender"><select value={guest.gender} onChange={(e) => setGuest({ ...guest, gender: e.target.value })} className={inp}><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select></Field>
              <Field label="Nationality"><input value={guest.nationality} onChange={(e) => setGuest({ ...guest, nationality: e.target.value })} className={inp} /></Field>
              <Field label="Purpose of visit"><select value={guest.purpose_of_visit} onChange={(e) => setGuest({ ...guest, purpose_of_visit: e.target.value })} className={inp}><option>Leisure</option><option>Business</option><option>Wedding</option><option>Medical</option><option>Family</option></select></Field>
              <Field label="ID type"><select value={guest.id_type} onChange={(e) => setGuest({ ...guest, id_type: e.target.value })} className={inp}><option value="">—</option><option>Aadhaar</option><option>Passport</option><option>Driving licence</option><option>PAN</option><option>Voter ID</option></select></Field>
              <Field label="ID number"><input value={guest.id_number} onChange={(e) => setGuest({ ...guest, id_number: e.target.value })} className={inp} /></Field>
              <Field label="Address" span2><input value={guest.address} onChange={(e) => setGuest({ ...guest, address: e.target.value })} className={inp} /></Field>
            </div>
          </section>
        </div>

        {/* Right sidebar: billing summary */}
        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
            <h3 className="font-display font-bold text-slate-900 text-sm flex items-center gap-2"><Receipt size={14} className="brand-pink" /> Billing summary</h3>
            <div className="mt-4 space-y-2 text-sm">
              <Line label={`Unit charges (${rooms.length} × ${nights}n)`} value={fmt(subtotal)} />
              {rateInclusive && effectiveTaxRate > 0 && (
                <p className="text-[10px] text-slate-400 -mt-1 leading-tight">Back-solved from gross ₹{fmt(gross)} @ {effectiveTaxRate}% incl.</p>
              )}
              <div className="flex justify-between items-center py-1">
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <span>Tax</span>
                  <input data-testid="nr-tax-rate" type="number" min="0" max="30" value={taxRate}
                    onChange={(e) => { setTaxAuto(false); setTaxRate(parseFloat(e.target.value || "0")); }}
                    disabled={taxExempt} className="w-14 border border-slate-200 rounded px-2 py-0.5 text-xs font-mono text-right focus:outline-none disabled:bg-slate-50" />
                  <span className="text-slate-400">%</span>
                  {taxAuto && !taxExempt && taxSlabs.length > 0 && (
                    <span data-testid="nr-tax-auto-badge" className="text-[9px] uppercase tracking-wider font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">Auto · per-night ₹{fmt(maxNightly)}</span>
                  )}
                  {!taxAuto && !taxExempt && (
                    <button type="button" data-testid="nr-tax-reset-auto" onClick={() => setTaxAuto(true)}
                      className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 underline hover:text-slate-800">Reset auto</button>
                  )}
                </div>
                <span className={`font-mono ${taxExempt ? "text-slate-300 line-through" : "text-slate-700"}`}>₹{fmt(tax)}</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-500 pb-2 border-b border-slate-100">
                <input data-testid="nr-tax-exempt" type="checkbox" checked={taxExempt} onChange={(e) => setTaxExempt(e.target.checked)} /> Tax exempt (e.g. non-GST corporate)
              </label>
              <Line label="Total" value={fmt(total)} bold />
            </div>

            <div className="mt-5">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-2">Bill to</p>
              <div className="flex gap-2">
                {["Guest", "Company", "Travel Agent"].map((b) => (
                  <button key={b} data-testid={`nr-bill-${b}`} onClick={() => setBillTo(b)} className={`flex-1 px-3 py-2 text-[11px] font-semibold rounded-full border ${billTo === b ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{b}</button>
                ))}
              </div>
              {billTo === "Company" && (
                <div className="mt-3 space-y-2">
                  <input data-testid="nr-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company / Organisation name *" className={inp} />
                  <input data-testid="nr-tax-id" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="Tax ID / GSTIN (e.g. 29ABCDE1234F1Z5)" className={`${inp} font-mono uppercase`} />
                  <p className="text-[10px] text-slate-500">Company name and Tax ID / GSTIN will print on the Tax Invoice under Bill To.</p>
                </div>
              )}
              {billTo === "Travel Agent" && ta && (
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] text-slate-600">
                  Billing to <b>{ta.name}</b>{ta.tax_id ? <> · GSTIN <b className="font-mono">{ta.tax_id}</b></> : <span className="text-amber-700"> · No GSTIN saved on this agent</span>}
                </div>
              )}
            </div>

            <div className="mt-5 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-3">
              <p className="text-[10px] uppercase font-semibold tracking-wider brand-pink mb-2">Travel Agent (optional)</p>
              <select
                data-testid="nr-ta-picker"
                value={taId}
                onChange={(e) => {
                  setTaId(e.target.value);
                  if (e.target.value) {
                    if (businessSource !== "Travel Agent") setBusinessSource("Travel Agent");
                    setBillTo("Travel Agent");
                  }
                }}
                className={inp}
              >
                <option value="">— No travel agent —</option>
                {travelAgents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} · {a.commission_plan === "percent" ? `${a.commission_value}%` : `₹${a.commission_value} flat`}{a.kind === "online" ? " · online" : ""}</option>
                ))}
              </select>
              {ta ? (
                <p data-testid="nr-ta-preview" className="text-[11px] text-slate-700 mt-2">
                  Commission preview: <b>₹{fmt(taCommission)}</b> on subtotal ₹{fmt(subtotal)} — logs to <b>{ta.name}</b>'s ledger the moment you confirm.
                </p>
              ) : (
                travelAgents.length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-2">Leave blank if this booking has no travel agent.</p>
                )
              )}
              {!travelAgents.length && (
                <p className="text-[11px] text-slate-500 mt-2">No agents yet — add one in the Travel Agents tab and it will appear here for every future reservation.</p>
              )}
            </div>

            <div className="mt-5 space-y-3">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Advance payment</p>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="nr-pay-amt" placeholder="Amount ₹" inputMode="decimal" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value.replace(/[^\d.]/g, "") })} className={inp} />
                <select value={payment.mode} onChange={(e) => setPayment({ ...payment, mode: e.target.value })} className={inp}>
                  {["UPI", "Cash", "Card", "Bank transfer", "Other"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <input placeholder="Remark (optional)" value={payment.remark} onChange={(e) => setPayment({ ...payment, remark: e.target.value })} className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <button className="text-[10px] font-semibold py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => setPayment({ ...payment, amount: String(total) })}>Full payment</button>
                <button className="text-[10px] font-semibold py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => setPayment({ ...payment, amount: String(Math.round(total * 0.2)) })}>20% advance</button>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 flex justify-between text-lg">
              <span className="font-display font-bold text-slate-900">Due amount</span>
              <span className={`font-display font-black ${due > 0 ? "text-red-500" : "text-emerald-600"}`}>₹{fmt(due)}</span>
            </div>

            <button data-testid="nr-submit" onClick={submit} disabled={busy}
              className="btn-brand w-full rounded-full py-3.5 text-sm font-bold mt-5 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Confirm reservation
            </button>
            <p className="text-[10px] text-slate-400 text-center mt-2 leading-tight">Double Booking Guard runs on every unit · rollback happens automatically if any unit fails.</p>
          </div>
        </aside>
      </div>
    </div>
    </Portal>
  );
}

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";

const Field = ({ label, children, span2 }) => (
  <div className={span2 ? "md:col-span-2 lg:col-span-3" : ""}>
    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Line = ({ label, value, bold }) => (
  <div className={`flex justify-between items-center py-1 ${bold ? "text-slate-900 pt-2 mt-1 border-t border-slate-100 font-bold text-base" : "text-slate-600"}`}>
    <span>{label}</span>
    <span className="font-mono">₹{value}</span>
  </div>
);
